import { useMemo } from "react";

import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  useCategories,
  usePermissions,
  type PermissionRow,
  type UserRow,
} from "@/lib/rbac";

/**
 * Read-only view of everything a user can actually do, grouped by category →
 * feature, with chips per action and source attribution on hover.
 *
 * Mirrors the backend resolution rule (service.expand_category_grants):
 * a category grant covers `read` (and `write` when access=write); any other
 * action only ever arrives via an explicit role permission or direct grant.
 */

function humanize(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function actionVariant(action: string): NonNullable<BadgeProps["variant"]> {
  if (action === "read") return "info";
  if (action === "write") return "success";
  return "warning"; // admin / investigate / agent.* — elevated, stands out
}

interface EffectiveGrant {
  perm: PermissionRow;
  sources: string[];
}

export function EffectivePermissions({ user }: { user: UserRow }) {
  const catalog = usePermissions();
  const categories = useCategories();

  const isSuperuser = user.roles.some((r) => r.key === "superuser");

  const byCategory = useMemo(() => {
    if (!catalog.data || !categories.data) return null;

    // permission key → list of human-readable sources
    const sources = new Map<string, string[]>();
    const add = (key: string, source: string) => {
      const cur = sources.get(key);
      if (cur) {
        if (!cur.includes(source)) cur.push(source);
      } else {
        sources.set(key, [source]);
      }
    };

    for (const p of user.direct_permissions) add(p.key, "Direct grant");
    for (const role of user.roles) {
      for (const p of role.permissions) add(p.key, `Role: ${role.name}`);
      for (const grant of role.category_grants) {
        const cat = categories.data.find((c) => c.name === grant.category);
        if (!cat) continue;
        for (const p of catalog.data) {
          if (!cat.features.includes(p.feature)) continue;
          const covered =
            p.action === "read" || (grant.access === "write" && p.action === "write");
          if (covered) add(p.key, `${role.name} · ${grant.category}: ${grant.access}`);
        }
      }
    }

    // category → feature → grants, in sidebar order
    const out: { category: string; features: { feature: string; grants: EffectiveGrant[] }[] }[] =
      [];
    for (const cat of categories.data) {
      const features: { feature: string; grants: EffectiveGrant[] }[] = [];
      for (const feature of cat.features) {
        const grants = catalog.data
          .filter((p) => p.feature === feature && sources.has(p.key))
          .map((p) => ({ perm: p, sources: sources.get(p.key)! }))
          .sort((a, b) => {
            const rank = (x: string) => (x === "read" ? 0 : x === "write" ? 1 : 2);
            return (
              rank(a.perm.action) - rank(b.perm.action) ||
              a.perm.action.localeCompare(b.perm.action)
            );
          });
        if (grants.length) features.push({ feature, grants });
      }
      if (features.length) out.push({ category: cat.name, features });
    }
    return out;
  }, [user, catalog.data, categories.data]);

  if (isSuperuser) {
    return (
      <div className="rounded-md border border-power/40 bg-power-light px-3 py-2.5 text-sm">
        <span className="font-semibold">Superuser</span> — full access to every
        feature; individual permissions don&apos;t apply.
      </div>
    );
  }

  if (!byCategory) {
    return <p className="text-sm text-muted-foreground">Loading permissions…</p>;
  }

  if (byCategory.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No permissions. Assign a role or add a direct grant below.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {byCategory.map(({ category, features }) => (
        <div key={category} className="rounded-md border">
          <div className="border-b bg-muted/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {category}
          </div>
          <div className="flex flex-col divide-y">
            {features.map(({ feature, grants }) => (
              <div key={feature} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="text-sm">{humanize(feature)}</span>
                <span className="flex flex-wrap justify-end gap-1">
                  {grants.map(({ perm, sources: srcs }) => (
                    <Badge
                      key={perm.id}
                      variant={actionVariant(perm.action)}
                      title={`${perm.key}\nvia ${srcs.join("; ")}`}
                    >
                      {perm.action}
                    </Badge>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="text-[11px] text-muted-foreground">
        Hover a chip to see where the permission comes from.
      </p>
    </div>
  );
}
